import { assertVaultKeyMaterial } from "./secure-vault";

export const TRUSTED_DEVICE_RECORD_VERSION = 1 as const;

const DATABASE_NAME = "cave-personnelle-trusted-device";
const DATABASE_VERSION = 1;
const STORE_NAME = "trusted-device";
const RECORD_KEY = "current";
const CHANNEL_NAME = "ma-cave-trusted-device";
const SIGNAL_STORAGE_KEY = "ma-cave:trusted-device-signal";
const RESTORE_SUSPENDED_KEY = "ma-cave:trusted-restore-suspended";

export type TrustedDeviceSignal = "forgotten" | "locked";

export interface TrustedDeviceRecord {
  version: typeof TRUSTED_DEVICE_RECORD_VERSION;
  material: CryptoKey;
  createdAt: number;
}

export interface TrustedDeviceStore {
  read(): Promise<unknown | null>;
  write(record: TrustedDeviceRecord): Promise<void>;
  delete(): Promise<void>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validateRecord(value: unknown): TrustedDeviceRecord {
  if (
    !isRecord(value) ||
    Object.keys(value).sort().join(",") !== "createdAt,material,version" ||
    value.version !== TRUSTED_DEVICE_RECORD_VERSION ||
    typeof value.createdAt !== "number" ||
    !Number.isSafeInteger(value.createdAt) ||
    value.createdAt <= 0
  ) {
    throw new TypeError("La donnée de confiance n’est pas reconnue.");
  }
  assertVaultKeyMaterial(value.material);
  return {
    version: TRUSTED_DEVICE_RECORD_VERSION,
    material: value.material,
    createdAt: value.createdAt,
  };
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Lecture IndexedDB impossible."));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Écriture IndexedDB impossible."));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("Écriture IndexedDB annulée."));
  });
}

export class IndexedDbTrustedDeviceStore implements TrustedDeviceStore {
  private databasePromise: Promise<IDBDatabase> | null = null;

  constructor(
    private readonly factory: IDBFactory,
    private readonly databaseName = DATABASE_NAME,
  ) {}

  private database(): Promise<IDBDatabase> {
    if (this.databasePromise) return this.databasePromise;
    this.databasePromise = new Promise((resolve, reject) => {
      const request = this.factory.open(this.databaseName, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => {
        request.result.onversionchange = () => request.result.close();
        resolve(request.result);
      };
      request.onerror = () =>
        reject(request.error ?? new Error("Ouverture IndexedDB impossible."));
      request.onblocked = () =>
        reject(new Error("Mise à niveau de la confiance locale bloquée."));
    });
    return this.databasePromise;
  }

  async read(): Promise<unknown | null> {
    const transaction = (await this.database()).transaction(
      STORE_NAME,
      "readonly",
    );
    const value = await requestResult(
      transaction.objectStore(STORE_NAME).get(RECORD_KEY),
    );
    return value === undefined ? null : value;
  }

  async write(record: TrustedDeviceRecord): Promise<void> {
    const validated = validateRecord(record);
    const transaction = (await this.database()).transaction(
      STORE_NAME,
      "readwrite",
    );
    transaction.objectStore(STORE_NAME).put(validated, RECORD_KEY);
    await transactionDone(transaction);
  }

  async delete(): Promise<void> {
    const transaction = (await this.database()).transaction(
      STORE_NAME,
      "readwrite",
    );
    transaction.objectStore(STORE_NAME).delete(RECORD_KEY);
    await transactionDone(transaction);
  }
}

export class MemoryTrustedDeviceStore implements TrustedDeviceStore {
  private record: unknown | null = null;

  async read(): Promise<unknown | null> {
    return this.record;
  }

  async write(record: TrustedDeviceRecord): Promise<void> {
    this.record = validateRecord(record);
  }

  async delete(): Promise<void> {
    this.record = null;
  }

  async replaceRawForTest(value: unknown | null): Promise<void> {
    this.record = value;
  }
}

let defaultStore: TrustedDeviceStore | null | undefined;

export function getTrustedDeviceStore(): TrustedDeviceStore | null {
  if (defaultStore !== undefined) return defaultStore;
  if (typeof indexedDB === "undefined") {
    defaultStore = null;
    return defaultStore;
  }
  defaultStore = new IndexedDbTrustedDeviceStore(indexedDB);
  return defaultStore;
}

export async function readTrustedDeviceMaterial(
  store = getTrustedDeviceStore(),
): Promise<CryptoKey | null> {
  if (!store) return null;
  const raw = await store.read();
  if (raw === null) return null;
  try {
    return validateRecord(raw).material;
  } catch {
    // Lecture volontairement pure : VaultGate s’exécute avant l’acquisition
    // du Web Lock et ne doit jamais effacer la confiance d’un autre onglet.
    return null;
  }
}

export async function storeTrustedDeviceMaterial(
  material: CryptoKey,
  store = getTrustedDeviceStore(),
): Promise<void> {
  if (!store) {
    throw new Error("La persistance cryptographique est indisponible.");
  }
  assertVaultKeyMaterial(material);
  await store.write({
    version: TRUSTED_DEVICE_RECORD_VERSION,
    material,
    createdAt: Date.now(),
  });
  resumeTrustedRestore();
}

export async function forgetTrustedDeviceMaterial(
  store = getTrustedDeviceStore(),
): Promise<void> {
  if (store) await store.delete();
  resumeTrustedRestore();
}

function writeRestoreFlag(storage: Storage | undefined, value: boolean): void {
  if (!storage) return;
  try {
    if (value) storage.setItem(RESTORE_SUSPENDED_KEY, "true");
    else storage.removeItem(RESTORE_SUSPENDED_KEY);
  } catch {
    // Le verrou en mémoire et le signal inter-onglets restent actifs.
  }
}

function browserStorage(kind: "localStorage" | "sessionStorage"): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window[kind];
  } catch {
    return undefined;
  }
}

export function suspendTrustedRestore(): void {
  if (typeof window === "undefined") return;
  writeRestoreFlag(browserStorage("localStorage"), true);
  writeRestoreFlag(browserStorage("sessionStorage"), true);
}

export function resumeTrustedRestore(): void {
  if (typeof window === "undefined") return;
  writeRestoreFlag(browserStorage("localStorage"), false);
  writeRestoreFlag(browserStorage("sessionStorage"), false);
}

export function isTrustedRestoreSuspended(): boolean {
  if (typeof window === "undefined") return false;
  for (const storage of [
    browserStorage("localStorage"),
    browserStorage("sessionStorage"),
  ]) {
    if (!storage) continue;
    try {
      if (storage.getItem(RESTORE_SUSPENDED_KEY) === "true") return true;
    } catch {
      // Essayer l’autre stockage avant de conclure.
    }
  }
  return false;
}

function signalPayload(signal: TrustedDeviceSignal): string {
  const nonce = typeof globalThis.crypto?.randomUUID === "function"
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${globalThis.performance?.now() ?? 0}`;
  return JSON.stringify({ version: 1, signal, nonce });
}

function parsedSignal(value: unknown): TrustedDeviceSignal | null {
  let candidate = value;
  if (typeof value === "string") {
    try {
      candidate = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  if (
    !isRecord(candidate) ||
    candidate.version !== 1 ||
    (candidate.signal !== "forgotten" && candidate.signal !== "locked") ||
    typeof candidate.nonce !== "string" ||
    candidate.nonce.length > 128
  ) {
    return null;
  }
  return candidate.signal;
}

export function broadcastTrustedDeviceSignal(signal: TrustedDeviceSignal): void {
  if (typeof window === "undefined") return;
  const payload = signalPayload(signal);
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(JSON.parse(payload));
    channel.close();
  } catch {
    // L’événement storage ci-dessous sert de solution de repli.
  }
  try {
    browserStorage("localStorage")?.setItem(SIGNAL_STORAGE_KEY, payload);
  } catch {
    // Le verrouillage de l’onglet courant reste prioritaire.
  }
}

export function subscribeTrustedDeviceSignals(
  subscriber: (signal: TrustedDeviceSignal) => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  let channel: BroadcastChannel | null = null;
  const notify = (value: unknown) => {
    const signal = parsedSignal(value);
    if (signal) subscriber(signal);
  };
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.addEventListener("message", (event) => notify(event.data));
  } catch {
    channel = null;
  }
  const handleStorage = (event: StorageEvent) => {
    if (event.key === SIGNAL_STORAGE_KEY && event.newValue) notify(event.newValue);
  };
  window.addEventListener("storage", handleStorage);
  return () => {
    channel?.close();
    window.removeEventListener("storage", handleStorage);
  };
}
